import { useContext, useEffect, useState } from 'react';
import * as z from 'zod';
import { getRouteApi } from '@tanstack/react-router';

import config from '@/config';

import { AppContext } from '@/App.context';
import { saveTokens } from '@/templates/basic/callback/Callback.hooks';
import { clearTokens } from '@/templates/basic/auth/Auth.hooks';

const routeApi = getRouteApi('/callback');

export const callbackSearchSchema = z.object({
	code: z.string(),
});

type CallbackSearch = z.infer<typeof callbackSearchSchema>;

export function useCallback(): {
	isLoading: boolean;
	error: Error | null;
	isSubscribed?: boolean;
	url: string;
	code: string | null;
} {
	const routeSearch: CallbackSearch = routeApi.useSearch();
	const { code } = routeSearch;
	const [url, setUrl] = useState('');

	const { kv, clearCache } = useContext(AppContext);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		if (!code) {
			setError(new Error('Missing code'));
			setIsLoading(false);
			return;
		}

		handleRequest(code)
			.then(data => {
				kv.clear();
				clearTokens();
				saveTokens(data);
				setUrl('/');
				setIsLoading(false);
			})
			.catch(e => {
				setError(e);
				setUrl('/');
				setIsLoading(false);
			});
	}, [code]);
	return {
		isLoading,
		error,
		url,
		code,
	};
}

async function handleRequest(code: string) {
	const redirecUrl = [
		location.protocol,
		'//',
		location.host,
		location.pathname,
	].join('');

	const params = new URLSearchParams({
		code: `${code}`,
		redirect_uri: redirecUrl,
	});

	const authUrl = [
		config.build.host,
		'/a360/magshop/authorize',
		'?',
		params.toString(),
	].join('');

	const r = await fetch(authUrl, {
		method: 'GET',
		headers: {
			'app-key': config.build.appKey,
			'Content-Type': 'application/json',
		},
	});

	if (!r.ok) {
		try {
			const data = await r.json();
			if (data?.error) {
				throw new Error(data.error);
			}
			throw new Error('An error happened authenticating the user');
		} catch (e) {
			throw new Error('An error happened authenticating the user');
		}
	}

	return await r.json();
}
